import { useCallback, useEffect, useState } from 'react'
import { Loader2, Plus, Search, Undo2 } from 'lucide-react'
import { useNotification } from '../../../contexts/NotificationContext'
import {
  SUPPRESSION_LABELS,
  addSuppression,
  fetchSuppressions,
  removeSuppression,
} from '../../../services/crmService'
import { Badge } from '../../ui/badge'
import { Button } from '../../ui/button'
import { ConfirmDialog } from '../../ui/confirm-dialog'
import { Input } from '../../ui/input'

const PAGE_SIZE = 50

const REASON_VARIANT = {
  unsubscribed: 'muted',
  bounced: 'orange',
  complained: 'magenta',
  manual: 'blue',
}

// A bounce or a spam complaint is the mailbox provider's verdict, not ours.
// Only entries an admin created by hand can be lifted from here.
const LIFTABLE = new Set(['manual', 'unsubscribed'])

export default function SuppressionsView() {
  const notify = useNotification()
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [search, setSearch] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(true)
  const [newEmail, setNewEmail] = useState('')
  const [adding, setAdding] = useState(false)
  const [confirmLift, setConfirmLift] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const result = await fetchSuppressions({ search: appliedSearch, reason, page, pageSize: PAGE_SIZE })
      setRows(result.suppressions)
      setTotal(result.total)
    } catch (err) {
      notify.error(err.message || 'Could not load the suppression list')
    } finally {
      setLoading(false)
    }
  }, [appliedSearch, reason, page, notify])

  useEffect(() => { load() }, [load])

  const add = async (e) => {
    e.preventDefault()
    if (!newEmail.trim()) return
    setAdding(true)
    try {
      await addSuppression(newEmail)
      notify.success(`${newEmail.trim().toLowerCase()} will never be mailed`)
      setNewEmail('')
      setPage(0)
      await load()
    } catch (err) {
      notify.error(err.message || 'Could not add that address')
    } finally {
      setAdding(false)
    }
  }

  const lift = async (row) => {
    try {
      await removeSuppression(row.id)
      notify.success(`${row.email} can receive campaigns again`)
      await load()
    } catch (err) {
      notify.error(err.message || 'Could not lift that suppression')
    }
  }

  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1)

  return (
    <div className="space-y-4">
      <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
        Every address here is excluded from every campaign, permanently and
        automatically — it survives re-syncs, imports and contact deletion.
        Unsubscribes and admin opt-outs can be lifted; bounces and spam
        complaints can&rsquo;t, because that verdict came from the recipient&rsquo;s
        mail provider.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <form
          onSubmit={(e) => { e.preventDefault(); setPage(0); setAppliedSearch(search) }}
          className="relative flex-1 min-w-[220px]"
        >
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search suppressed addresses"
            className="pl-9"
            aria-label="Search suppressions"
          />
        </form>

        <select
          value={reason}
          onChange={(e) => { setPage(0); setReason(e.target.value) }}
          aria-label="Filter by reason"
          className="h-10 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="">All reasons</option>
          {Object.entries(SUPPRESSION_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>

        <form onSubmit={add} className="flex gap-2">
          <Input
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            type="email"
            placeholder="Add an address"
            aria-label="Address to suppress"
            className="w-56"
          />
          <Button type="submit" variant="outline" disabled={adding || !newEmail.trim()}>
            {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          </Button>
        </form>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-2 font-medium">Email</th>
              <th className="px-4 py-2 font-medium">Reason</th>
              <th className="px-4 py-2 font-medium">Detail</th>
              <th className="px-4 py-2 font-medium">Since</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
                </td>
              </tr>
            )}

            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">
                  {total === 0 && !appliedSearch && !reason
                    ? 'Nobody is suppressed. Unsubscribes and bounces land here automatically.'
                    : 'No suppressions match those filters.'}
                </td>
              </tr>
            )}

            {!loading && rows.map((row) => (
              <tr key={row.id} className="border-t border-border">
                <td className="px-4 py-2 font-medium">{row.email}</td>
                <td className="px-4 py-2">
                  <Badge variant={REASON_VARIANT[row.reason] ?? 'muted'}>
                    {SUPPRESSION_LABELS[row.reason] ?? row.reason}
                  </Badge>
                </td>
                <td className="px-4 py-2 text-muted-foreground">{row.detail || '—'}</td>
                <td className="px-4 py-2 text-muted-foreground">
                  {new Date(row.created_at).toLocaleDateString()}
                </td>
                <td className="px-4 py-2 text-right">
                  {LIFTABLE.has(row.reason) && (
                    <Button variant="ghost" size="sm" onClick={() => setConfirmLift(row)}>
                      <Undo2 className="mr-1.5 h-3.5 w-3.5" />Lift
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total.toLocaleString()}
          </span>
          <span className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={page >= lastPage} onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </span>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmLift}
        onOpenChange={(open) => { if (!open) setConfirmLift(null) }}
        title="Let this address receive campaigns again?"
        description={
          confirmLift
            ? `${confirmLift.email} chose to stop receiving email. Only lift this if they've asked to be put back on the list.`
            : ''
        }
        confirmText="Lift suppression"
        onConfirm={() => { const row = confirmLift; setConfirmLift(null); lift(row) }}
      />
    </div>
  )
}
